import React, { useMemo, useRef, useState } from "react";
import {
  Database,
  Download,
  Upload,
  RefreshCw,
  FileJson,
  ShieldAlert,
  Trash2,
  CheckSquare,
  Square,
} from "lucide-react";
import {
  createBackupPayload,
  importBackupMerge,
  parseBackupFile,
  restoreBackupReplace,
  resetSelectedCollections,
} from "../services/backupService";
import { usePanelTheme } from "../context/themeContext";
import { notify } from "../utils/notify";

export default function BackupRestorePanel({ language = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const fileInputRef = useRef(null);

  const labels = useMemo(() => {
    const dict = {
      de: {
        title: "Backup & Restore",
        subtitle: "Lokale Sicherung, Wiederherstellung und selektives Zurücksetzen der Datenbank.",
        downloadTitle: "Backup herunterladen",
        downloadText: "Erstellt eine vollständige lokale JSON-Sicherung für PC / Archiv / Migration.",
        downloadBtn: "Backup downloaden",
        importTitle: "Backup laden",
        importText: "Wähle eine bestehende JSON-Datei und prüfe zuerst die enthaltenen Daten.",
        chooseFile: "Datei auswählen",
        selectedFile: "Ausgewählte Datei",
        preview: "Vorschau",
        noFile: "Noch keine Datei gewählt.",
        collections: "Sammlungen",
        entries: "Einträge",
        replaceTitle: "Restore Replace",
        replaceText: "Ersetzt die vorhandenen Daten komplett mit dem Inhalt der geladenen Backup-Datei.",
        replaceBtn: "Komplett wiederherstellen",
        mergeTitle: "Merge Import",
        mergeText: "Importiert die Daten zusätzlich zur vorhandenen Datenbank.",
        mergeBtn: "Zusätzlich importieren",
        loaded: "Backup-Datei geladen.",
        downloadOk: "Backup wurde heruntergeladen.",
        mergeOk: "Backup wurde erfolgreich importiert.",
        replaceOk: "Backup wurde erfolgreich wiederhergestellt.",
        parseFail: "Backup-Datei konnte nicht gelesen werden.",
        noPayload: "Bitte zuerst eine Backup-Datei auswählen.",
        warning: "Achtung: Replace überschreibt die aktuellen Daten.",
        resetTitle: "Selektiver Reset",
        resetText: "Wähle Kategorien aus, die vollständig aus der Datenbank gelöscht werden sollen.",
        resetBtn: "Ausgewählte Kategorien löschen",
        resetOk: "Die ausgewählten Kategorien wurden gelöscht.",
        resetNone: "Bitte wähle mindestens eine Kategorie aus.",
        resetWarning: "Möchtest du wirklich diese Kategorien löschen?",
        selectAll: "Alle wählen",
        clearAll: "Auswahl löschen",
        deleting: "Wird gelöscht...",
      },
      sq: {
        title: "Backup & Restore",
        subtitle: "Ruajtje lokale, rikthim dhe reset selektiv i databazës.",
        downloadTitle: "Shkarko backup",
        downloadText: "Krijon backup të plotë lokal për PC / arkivë / migrim.",
        downloadBtn: "Shkarko backup",
        importTitle: "Ngarko backup",
        importText: "Zgjidh një file JSON ekzistues dhe shfaq paraprakisht çfarë përmban.",
        chooseFile: "Zgjidh file",
        selectedFile: "File i zgjedhur",
        preview: "Paraqitje",
        noFile: "Ende s'është zgjedhur file.",
        collections: "Koleksione",
        entries: "Hyrje",
        replaceTitle: "Restore Replace",
        replaceText: "Zëvendëson të dhënat ekzistuese me ato të backup-it të ngarkuar.",
        replaceBtn: "Rikthe komplet",
        mergeTitle: "Merge Import",
        mergeText: "Shton të dhënat e backup-it mbi databazën ekzistuese.",
        mergeBtn: "Importo sipër",
        loaded: "Backup file u ngarkua me sukses.",
        downloadOk: "Backup u shkarkua me sukses.",
        mergeOk: "Backup u importua me sukses.",
        replaceOk: "Backup u rikthye me sukses.",
        parseFail: "Nuk u lexua backup file.",
        noPayload: "Së pari zgjidh një backup file.",
        warning: "Kujdes: Replace i mbishkruan të dhënat aktuale.",
        resetTitle: "Reset selektiv",
        resetText: "Zgjidh kategoritë që dëshiron t’i fshish komplet nga databaza.",
        resetBtn: "Fshij kategoritë e zgjedhura",
        resetOk: "Kategoritë e zgjedhura u fshinë me sukses.",
        resetNone: "Zgjidh të paktën një kategori.",
        resetWarning: "A je i sigurt që dëshiron t’i fshish këto kategori?",
        selectAll: "Zgjidh të gjitha",
        clearAll: "Pastro zgjedhjen",
        deleting: "Duke fshirë...",
      },
    };
    return dict[language] || dict.de;
  }, [language]);

  const resetOptions = [
    { key: "income", label: "Income" },
    { key: "expenses", label: "Expenses" },
    { key: "payments", label: "Payments" },
    { key: "materials", label: "Materials" },
    { key: "workers", label: "Workers" },
    { key: "invoice_clients", label: "Invoice Clients" },
    { key: "invoices", label: "Invoices" },
    { key: "stats", label: "Monthly Stats" },
    { key: "settings/company", label: "Company Settings" },
  ];

  const [payload, setPayload] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [selectedResetItems, setSelectedResetItems] = useState([]);

  const softClass =
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-300"
      : "border-slate-200 bg-slate-50 text-slate-700";

  const handleDownload = async () => {
    setBusyAction("download");
    try {
      const nextPayload = await createBackupPayload();
      const fileName = `bauheld-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      const blob = new Blob([JSON.stringify(nextPayload, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      setPayload(nextPayload);
      setSummary(nextPayload?.summary || null);
      setSelectedFileName(fileName);
      notify.success(labels.downloadOk);
    } catch (error) {
      console.error("Backup download failed:", error);
      notify.error(error?.message || labels.parseFail);
    } finally {
      setBusyAction("");
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBusyAction("parse");
    try {
      const parsed = await parseBackupFile(file);
      setPayload(parsed.payload);
      setSummary(parsed.summary);
      setSelectedFileName(file.name || "");
      notify.success(labels.loaded);
    } catch (error) {
      console.error("Backup parse failed:", error);
      notify.error(error?.message || labels.parseFail);
    } finally {
      setBusyAction("");
      event.target.value = "";
    }
  };

  const handleMerge = async () => {
    if (!payload) {
      notify.error(labels.noPayload);
      return;
    }

    setBusyAction("merge");
    try {
      await importBackupMerge(payload);
      notify.success(labels.mergeOk);
    } catch (error) {
      console.error("Backup merge failed:", error);
      notify.error(error?.message || labels.parseFail);
    } finally {
      setBusyAction("");
    }
  };

  const handleReplace = async () => {
    if (!payload) {
      notify.error(labels.noPayload);
      return;
    }

    const ok = window.confirm(labels.warning);
    if (!ok) return;

    setBusyAction("replace");
    try {
      await restoreBackupReplace(payload);
      notify.success(labels.replaceOk);
    } catch (error) {
      console.error("Backup replace failed:", error);
      notify.error(error?.message || labels.parseFail);
    } finally {
      setBusyAction("");
    }
  };

  const toggleResetItem = (key) => {
    setSelectedResetItems((prev) =>
      prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key]
    );
  };

  const selectAllResetItems = () => {
    setSelectedResetItems(resetOptions.map((item) => item.key));
  };

  const clearAllResetItems = () => {
    setSelectedResetItems([]);
  };

  const handleSelectiveReset = async () => {
    if (!selectedResetItems.length) {
      notify.error(labels.resetNone);
      return;
    }

    const selectedLabels = resetOptions
      .filter((item) => selectedResetItems.includes(item.key))
      .map((item) => item.label)
      .join(", ");

    const confirmed = window.confirm(
      `${labels.resetWarning}\n\n${selectedLabels}`
    );

    if (!confirmed) return;

    setBusyAction("reset");
    try {
      await resetSelectedCollections(selectedResetItems);
      notify.success(labels.resetOk);
      setSelectedResetItems([]);
    } catch (error) {
      console.error("Selective reset failed:", error);
      notify.error(error?.message || labels.parseFail);
    } finally {
      setBusyAction("");
    }
  };

  return (
    <div className={`rounded-[28px] border p-5 shadow-sm ${currentTheme.surface}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ${softClass}`}>
            <Database className="h-3.5 w-3.5" />
            Database
          </div>
          <h3 className={`mt-3 text-lg font-black ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
            {labels.title}
          </h3>
          <p className={`mt-1 text-sm ${currentTheme.muted}`}>{labels.subtitle}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <div className={`rounded-[24px] border p-4 ${softClass}`}>
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-slate-900 p-2.5 text-white">
              <Download className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-black">{labels.downloadTitle}</div>
              <div className="mt-1 text-sm opacity-80">{labels.downloadText}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDownload}
            disabled={!!busyAction}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busyAction === "download" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {labels.downloadBtn}
          </button>
        </div>

        <div className={`rounded-[24px] border p-4 ${softClass}`}>
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-slate-900 p-2.5 text-white">
              <Upload className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-black">{labels.importTitle}</div>
              <div className="mt-1 text-sm opacity-80">{labels.importText}</div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleFileChange}
          />

          <button
            type="button"
            onClick={handleChooseFile}
            disabled={!!busyAction}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            {busyAction === "parse" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
            {labels.chooseFile}
          </button>

          <div className="mt-3 text-xs opacity-80">
            {labels.selectedFile}: <span className="font-semibold">{selectedFileName || labels.noFile}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <div className={`rounded-[24px] border p-4 ${softClass}`}>
            <div className="mb-3 text-sm font-black">{labels.preview}</div>

            {summary ? (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-inherit bg-white/70 p-3 dark:bg-slate-950/60">
                    <div className="text-xs uppercase tracking-[0.16em] opacity-70">{labels.collections}</div>
                    <div className="mt-1 text-2xl font-black">
                      {Object.keys(summary.breakdown || {}).length}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-inherit bg-white/70 p-3 dark:bg-slate-950/60">
                    <div className="text-xs uppercase tracking-[0.16em] opacity-70">{labels.entries}</div>
                    <div className="mt-1 text-2xl font-black">{summary.entries || 0}</div>
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(summary.breakdown || {}).map(([key, value]) => (
                    <div
                      key={key}
                      className="rounded-2xl border border-inherit bg-white/70 px-3 py-2 text-sm dark:bg-slate-950/60"
                    >
                      <span className="font-semibold">{key}</span>
                      <span className="float-right font-black">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-inherit p-4 text-sm opacity-70">
                {labels.noFile}
              </div>
            )}
          </div>

          <div className={`rounded-[24px] border p-4 ${softClass}`}>
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-red-600 p-2.5 text-white">
                <Trash2 className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-black">{labels.resetTitle}</div>
                <div className="mt-1 text-sm opacity-80">{labels.resetText}</div>
              </div>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {resetOptions.map((item) => (
                <label
                  key={item.key}
                  className="flex items-center gap-3 rounded-2xl border border-inherit bg-white/70 px-3 py-3 text-sm dark:bg-slate-950/60"
                >
                  <input
                    type="checkbox"
                    checked={selectedResetItems.includes(item.key)}
                    onChange={() => toggleResetItem(item.key)}
                    className="h-4 w-4"
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={selectAllResetItems}
                className="inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold"
              >
                <CheckSquare className="h-4 w-4" />
                {labels.selectAll}
              </button>

              <button
                type="button"
                onClick={clearAllResetItems}
                className="inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold"
              >
                <Square className="h-4 w-4" />
                {labels.clearAll}
              </button>
            </div>

            <button
              type="button"
              onClick={handleSelectiveReset}
              disabled={busyAction === "reset"}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busyAction === "reset" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {busyAction === "reset" ? labels.deleting : labels.resetBtn}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className={`rounded-[24px] border p-4 ${softClass}`}>
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-amber-500 p-2.5 text-white">
                <ShieldAlert className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-black">{labels.replaceTitle}</div>
                <div className="mt-1 text-sm opacity-80">{labels.replaceText}</div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleReplace}
              disabled={!!busyAction}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busyAction === "replace" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
              {labels.replaceBtn}
            </button>
          </div>

          <div className={`rounded-[24px] border p-4 ${softClass}`}>
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-emerald-600 p-2.5 text-white">
                <Upload className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-black">{labels.mergeTitle}</div>
                <div className="mt-1 text-sm opacity-80">{labels.mergeText}</div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleMerge}
              disabled={!!busyAction}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busyAction === "merge" ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {labels.mergeBtn}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
